import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { IAttachment, IPermitTypeField } from '~/client/graph'
import { AttachmentIcon } from '~/client/src/shared/components/icons/AttachmentIcon'
import { CameraIcon } from '~/client/src/shared/components/icons/CameraIcon'
import PhotoMarkupDialog from '~/client/src/shared/components/PhotoMarkup/PhotoMarkupDialog'
import { canMarkUpImage } from '~/client/src/shared/components/PhotoMarkup/photoMarkupModel'
import BaseStruxhubInput from '~/client/src/shared/components/StruxhubInputs/BaseStruxhubInput'
import FileAttachment from '~/client/src/shared/components/WorkflowCard/FileAttachment'
import IFilePreviewProperties from '~/client/src/shared/interfaces/IFilePreviewProperties'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import { SHOW_FULLSCREEN_PREVIEW } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFormFileUploadButton from './PermitFormFileUploadButton'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  parentInstanceId?: string
  parentValueIndex?: number

  isMobileEnv?: boolean
  eventsStore?: EventsStore
}

interface IState {
  markupIndex: number | null
  capturedPhoto: { file: File; url: string } | null
}

const uploadFilesIcon = (
  <AttachmentIcon
    size="sm"
    className="tw:!flex-none tw:text-md3-on-surface-variant"
  />
)
const cameraIcon = (
  <CameraIcon
    size="sm"
    className="tw:!flex-none tw:text-md3-on-surface-variant"
  />
)

@inject('isMobileEnv', 'eventsStore')
@observer
export default class PermitFormFileUploadField extends React.Component<
  IProps,
  IState
> {
  public state: IState = { markupIndex: null, capturedPhoto: null }

  public componentWillUnmount() {
    this.revokeCapturedPhotoUrl()
  }

  public render() {
    const {
      isViewMode,
      typeField,
      parentInstanceId,
      parentValueIndex,
      store,
      fieldsStore,
      isMobileEnv,
    } = this.props
    const { caption, isMandatory } = typeField

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={null}
        viewModeRenderer={this.renderViewMode}
      >
        <BaseStruxhubInput
          className="overflow-hidden"
          label={caption}
          isRequired={isMandatory}
          isValid={true}
          value={!!this.fieldValues?.length && '1'}
        >
          {() => (
            <div className="row y-end permit-form-fields-upload">
              <PermitFormFileUploadButton
                spacingClassName="mr8"
                acceptPattern=""
                isMultiple
                icon={uploadFilesIcon}
                label={Localization.translator.attachFiles}
                uploadFile={this.uploadAttachment}
              />
              {isMobileEnv && (
                <PermitFormFileUploadButton
                  spacingClassName="ml8"
                  acceptPattern="image/*"
                  isMultiple={false}
                  icon={cameraIcon}
                  label={Localization.translator.camera}
                  uploadFile={this.openCapturedPhoto}
                />
              )}
            </div>
          )}
        </BaseStruxhubInput>
        {this.attachmentElements}
        {this.markupDialog}
        {this.capturedPhotoDialog}
      </PermitBaseFormField>
    )
  }

  private renderViewMode = (value: IAttachment): JSX.Element => {
    return (
      <FileAttachment
        attachment={value}
        className="iconless-row permit-form-attachment tw:!mt-0"
      />
    )
  }

  private get attachmentElements(): JSX.Element[] {
    const { isViewMode } = this.props

    return this.fieldValues?.map((attachment, idx) => (
      <FileAttachment
        key={idx}
        attachment={attachment}
        removeAttachment={!isViewMode && this.removeAttachment.bind(this, idx)}
        customOpenFileHandler={
          !isViewMode && this.openAttachment.bind(this, idx)
        }
        className="iconless-row permit-form-attachment"
      />
    ))
  }

  private get markupDialog(): JSX.Element {
    const attachment = this.fieldValues?.[this.state.markupIndex]
    if (!attachment) {
      return null
    }

    return (
      <PhotoMarkupDialog
        imageUrl={attachment.url}
        fileName={attachment.fileName}
        isMobileEnv={this.props.isMobileEnv}
        onSave={this.replaceMarkedUpAttachment}
        onClose={this.closeMarkup}
      />
    )
  }

  private get capturedPhotoDialog(): JSX.Element {
    const { capturedPhoto } = this.state
    if (!capturedPhoto) {
      return null
    }

    return (
      <PhotoMarkupDialog
        imageUrl={capturedPhoto.url}
        fileName={capturedPhoto.file.name}
        originalFile={capturedPhoto.file}
        isMobileEnv={this.props.isMobileEnv}
        onSave={this.uploadCapturedPhoto}
        onClose={this.closeCapturedPhoto}
      />
    )
  }

  private openCapturedPhoto = async (file: File): Promise<void> => {
    this.revokeCapturedPhotoUrl()
    this.setState({
      capturedPhoto: { file, url: URL.createObjectURL(file) },
    })
  }

  private closeCapturedPhoto = () => {
    this.revokeCapturedPhotoUrl()
    this.setState({ capturedPhoto: null })
  }

  private revokeCapturedPhotoUrl() {
    const { capturedPhoto } = this.state
    if (capturedPhoto) {
      URL.revokeObjectURL(capturedPhoto.url)
    }
  }

  private openAttachment(
    markupIndex: number,
    files: IFilePreviewProperties[],
    currentIndex: number,
  ) {
    if (canMarkUpImage(files[currentIndex]?.fileName)) {
      this.setState({ markupIndex })
      return
    }

    this.props.eventsStore.dispatch(
      SHOW_FULLSCREEN_PREVIEW,
      files,
      currentIndex,
    )
  }

  private closeMarkup = () => {
    this.setState({ markupIndex: null })
  }

  private replaceMarkedUpAttachment = async (file: File): Promise<void> => {
    const {
      store,
      fieldsStore,
      typeField,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    await store.replaceAttachment(
      file,
      typeField,
      fieldsStore,
      this.state.markupIndex,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private get fieldValues(): IAttachment[] {
    const { typeField, fieldsStore, parentInstanceId, parentValueIndex } =
      this.props

    return fieldsStore.getFieldValues<IAttachment>(
      typeField.id,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private uploadAttachment = async (file: File): Promise<void> => {
    const {
      store,
      fieldsStore,
      typeField,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    await store.uploadAttachment(
      file,
      typeField,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private uploadCapturedPhoto = async (file: File): Promise<void> => {
    const {
      store,
      fieldsStore,
      typeField,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const isAttached = await store.uploadAttachment(
      file,
      typeField,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    )
    if (!isAttached) {
      throw new Error('Attachment upload returned no file')
    }
  }

  private removeAttachment(fieldIndex: number) {
    const { typeField, fieldsStore, parentInstanceId, parentValueIndex } =
      this.props

    fieldsStore.removeFieldValue(
      typeField.id,
      fieldIndex,
      parentInstanceId,
      parentValueIndex,
    )
  }
}
